import { getOrderShoppers, getOrderProducts, getOrderDelivery } from 'common/api/frontend';
import {
	OrderInfo,
	OrderDeliveryService,
	ProductApi,
	OrderProduct,
	Shopper,
	Location,
	LocationWithContact,
	DateFormatObject,
	DeliveryOption,
	OpeningHoursWithExceptions,
} from 'common/types';
import { min, chain } from 'lodash';
import moment from 'moment';
import * as ExcelJs from 'exceljs';
import { saveAs } from 'file-saver';
import { getSetChildren } from 'common/utils/productUtils';
import { TFunction } from 'i18next';
import { notUndefined } from 'common/utils/common';
import { getResponsiblePersonDetails } from 'common/utils/newRentalUtils';
import { getFormattedAddress } from 'common/modules/atoms/addresses';
import { isRentalProduct } from 'common/modules/orders';
import { timeslotToString } from 'common/modules/delivery/utils';
import { getBufferTimeAsMinutes } from 'common/modules/atoms/bufferTimes';

interface DeliveryExportTableRow {
	'Scheduled Time': string;
	Type: string;
	Address: string;
	Items: string;
	'Item ID': string;
	'Contact person': string;
	'Contact details': string;
	'Return to (if pick up)': string;
}

interface DeliveryData {
	orderShoppers: Shopper[];
	orderProducts: OrderProduct[];
	orderDelivery: OrderDeliveryService;
	deliveryType: 'DELIVERY' | 'PICKUP';
	rentalInfo: OrderInfo;
}

export const exportXLSX = async (
	deliveryData: {
		parsedDeliveryData: DeliveryExportTableRow[];
		carrier: string;
	}[],
	fileName: string,
	t: TFunction,
) => {
	const workbook = new ExcelJs.Workbook();

	if (deliveryData.length < 1) {
		return;
	}

	deliveryData.forEach(({ parsedDeliveryData, carrier }) => {
		const sortedDeliveryData = chain(parsedDeliveryData)
			.sortBy((sortedData) => sortedData['Scheduled Time'])
			.value();

		const worksheet = workbook.addWorksheet(carrier);

		sortedDeliveryData.forEach((sorted) => {
			worksheet.columns = [
				{
					header: t('delivery.export.scheduledTime', 'Scheduled Time'),
					key: 'time',
					width: 25,
				},
				{ header: t('delivery.export.type', 'Type'), key: 'type', width: 25 },
				{ header: t('delivery.export.address', 'Address'), key: 'address', width: 25 },
				{ header: t('delivery.export.items', 'Items'), key: 'items', width: 40 },
				{ header: t('delivery.export.itemId', 'Item ID'), key: 'itemID', width: 25 },
				{
					header: t('delivery.export.contactPerson', 'Contact person'),
					key: 'person',
					width: 25,
				},
				{
					header: t('delivery.export.contactDetails', 'Contact details'),
					key: 'details',
					width: 40,
				},
				{
					header: t('delivery.export.returnTo'),
					key: 'return',
					width: 40,
				},
			];

			return worksheet.addRow(Object.values(sorted));
		});

		worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
			row.eachCell({ includeEmpty: true }, (cell) => {
				cell.style = {
					...cell,
					font: { size: 15 },
					border: {
						top: { style: 'thin' },
						right: { style: 'thin' },
						bottom: { style: 'thin' },
						left: { style: 'thin' },
					},
					alignment: { wrapText: true, vertical: 'top' },
				};

				if (rowNumber <= 1) {
					cell.style = {
						...cell,
						font: {
							bold: true,
							size: 17,
						},
						alignment: {
							vertical: 'middle',
							horizontal: 'center',
						},
					};
				}
			});
		});
	});

	const buffer = await workbook.xlsx.writeBuffer();

	saveAs(new Blob([buffer]), `${fileName}.xlsx`);
};

export const getMinDeliveryBookingBuffer = (
	deliveryOptions: DeliveryOption[],
	timezone: string,
	openingHours: OpeningHoursWithExceptions,
): number => {
	const now = moment().toISOString();

	return (
		min(
			deliveryOptions.map((option) => {
				return getBufferTimeAsMinutes({
					value: option.handlingTimes?.preparationTime,
					from: now,
					timezone,
					openingHours,
					direction: 'before',
				});
			}),
		) ?? 0
	);
};

const deliveryTypeToString = (type: 'DELIVERY' | 'PICKUP', isSelfReturn: boolean, t: TFunction) =>
	type === 'DELIVERY'
		? t('delivery.delivery', 'Delivery')
		: isSelfReturn
		? t('delivery.selfReturn', 'Self-return')
		: t('delivery.pickup');

export const formatDeliveryRouteExport = (args: {
	orderData: {
		deliveries: DeliveryData[];
		carrier: string;
	}[];
	productsData: ProductApi[];
	t: TFunction;
	shopDateFormat: DateFormatObject;
	shopTimezone: string;
}) => {
	const { orderData, productsData, t, shopDateFormat, shopTimezone } = args;
	const data = orderData.map(({ deliveries, carrier }) => {
		const parsedDeliveryData = deliveries.map(
			({ orderShoppers, orderProducts, orderDelivery, deliveryType, rentalInfo }) => {
				const responsiblePerson = getResponsiblePersonDetails(
					rentalInfo.responsiblePerson,
					orderShoppers,
				);
				const returnLocation = rentalInfo.endLocation.address;
				const product = productsData.find((data) => !!data.id)!;
				const productNames = orderProducts
					.filter(productSupportsDeliveryType(deliveryType))
					.map((p) => {
						const isSet = !!p.set;
						const setProductsName = getSetChildren(product, productsData)
							?.map(({ name }) => name.def)
							.join(' \n');
						return isSet ? `${p.name.def}: \n${setProductsName}` : p.name.def;
					})
					.join(' \n');
				const isSet = !!orderProducts.find((p) => p.set);
				const productCodes = isSet
					? orderProducts
							.filter(productSupportsDeliveryType(deliveryType))
							.flatMap((p) =>
								p.setProducts.map((set) =>
									!!set.productCode
										? set.productCode
										: t('delivery.noProductCode', 'No product code'),
								),
							)
							.join('\n')
					: orderProducts
							.map((p) =>
								!!p.productCode ? p.productCode : t('delivery.noProductCode', 'No product code'),
							)
							.join('\n');
				return {
					'Scheduled Time':
						deliveryType === 'DELIVERY'
							? timeslotToString({
									timeslot: orderDelivery.to?.timeslot ?? null,
									shopDateFormat,
									timezone: shopTimezone,
							  })
							: timeslotToString({
									timeslot: orderDelivery.from?.timeslot ?? null,
									shopDateFormat,
									timezone: shopTimezone,
							  }),
					Type: deliveryTypeToString(deliveryType, !!orderDelivery?.from?.disabled, t),
					Address: getFormattedAddress(orderDelivery?.to?.location?.address),
					Items: productNames,
					'Item ID': productCodes,
					'Contact person': `${responsiblePerson.firstName} ${responsiblePerson.lastName}`,
					'Contact details': responsiblePerson.phone,
					'Return to (if pick up)': returnLocation,
				};
			},
		);
		return { parsedDeliveryData, carrier };
	});
	return data;
};

const productSupportsDeliveryType = (deliveryType: 'DELIVERY' | 'PICKUP') => (
	orderProduct: OrderProduct,
) => (deliveryType === 'PICKUP' ? isRentalProduct(orderProduct) : true);

export const getDeliveriesToday = (rentalsWithDelivery: OrderInfo[], day: moment.Moment) =>
	rentalsWithDelivery.filter((rental) =>
		moment(rental.services!.delivery!.to?.startDate).isSame(day, 'day'),
	);

export const getPickUpsToday = (rentalsWithPickUp: OrderInfo[], day: moment.Moment) =>
	rentalsWithPickUp.filter((rental) => moment(rental.returnTimeNext!).isSame(day, 'day'));

const getDeliveryListOrderDetails = (deliveryList: OrderInfo[], type: 'DELIVERY' | 'PICKUP') => {
	return Promise.all(deliveryList.map((delivery) => getDeliveryOrderDetails(delivery, type)));
};

const getDeliveryOrderDetails = async (
	delivery: OrderInfo,
	deliveryType: 'DELIVERY' | 'PICKUP',
): Promise<
	| {
			orderShoppers: Shopper[];
			orderProducts: OrderProduct[];
			orderDelivery: OrderDeliveryService;
			deliveryType: 'DELIVERY' | 'PICKUP';
			rentalInfo: OrderInfo;
	  }
	| undefined
> => {
	const [orderShoppers, orderProducts, orderDelivery, rentalInfo] = await Promise.all([
		getOrderShoppers(delivery.id, delivery.shopId),
		getOrderProducts(delivery.id, delivery.shopId),
		getOrderDelivery(delivery.id, delivery.shopId),
		delivery,
	]);

	if (!orderDelivery) return undefined;

	return { orderShoppers, orderProducts, orderDelivery, deliveryType, rentalInfo };
};

const deliveryAndPickUpDetails = async (delivery: OrderInfo[], pickUp: OrderInfo[]) => {
	const [deliveryList, pickUpList] = await Promise.all([
		getDeliveryListOrderDetails(delivery, 'DELIVERY'),
		getDeliveryListOrderDetails(pickUp, 'PICKUP'),
		delivery,
	]);

	return [...deliveryList, ...pickUpList];
};

const groupByCarrier = (deliveryRouteData: DeliveryData[], t: TFunction) => {
	return chain(deliveryRouteData)
		.groupBy(
			(deliveryData) =>
				(deliveryData.deliveryType === 'DELIVERY'
					? deliveryData.orderDelivery.to?.carrier?.name?.def
					: deliveryData.orderDelivery.from?.carrier?.name?.def) ??
				t('delivery.export.noCarrier', 'No carrier'),
		)
		.map((deliveries, carrier) => {
			return { deliveries, carrier };
		})
		.sortBy((carrier) => carrier.carrier)
		.value();
};

export const exportDeliveries = async (args: {
	rentalsWithDelivery: OrderInfo[];
	rentalsWithPickUp: OrderInfo[];
	startDate: moment.Moment | null;
	productData: ProductApi[];
	userLocations: [LocationWithContact] | Location[];
	t: TFunction;
	shopDateFormat: DateFormatObject;
	shopTimezone: string;
}) => {
	const {
		rentalsWithDelivery,
		rentalsWithPickUp,
		startDate,
		productData,
		userLocations,
		t,
		shopDateFormat,
		shopTimezone,
	} = args;
	userLocations.forEach(async (location) => {
		const locationRentalsWithDelivery = rentalsWithDelivery.filter(
			(rental) => rental.startLocation.id === location.id,
		);
		const locationRentalsWithPickUp = rentalsWithPickUp.filter(
			(rental) => rental.startLocation.id === location.id,
		);

		const deliveryRouteData = await deliveryAndPickUpDetails(
			getDeliveriesToday(locationRentalsWithDelivery, startDate!),
			getPickUpsToday(locationRentalsWithPickUp, startDate!),
		);

		const groupedDeliveryRouteData = groupByCarrier(deliveryRouteData.filter(notUndefined), t);

		const fileName = `${location.name}_delivery_list_${startDate?.format('DD-MM-YYYY')}`;

		exportXLSX(
			formatDeliveryRouteExport({
				orderData: groupedDeliveryRouteData,
				productsData: productData,
				shopDateFormat,
				shopTimezone,
				t,
			}),
			fileName,
			t,
		);
	});
};
